/**
 * The model behind inline list editing.
 *
 * A column becomes editable by carrying an `edit` spec in its `meta`. The spec
 * says how to read the field off a row, what kind of editor to open, and what
 * API payload sets it. Everything else — opening the editor, staging the
 * change, keyboard movement — is handled by `DataTable`, so a list only ever
 * describes its fields.
 *
 * Edits are staged rather than sent: each committed cell keeps both the value
 * (so the cell can show what it will become) and the patch it produces. A save
 * merges every patch of a row into one payload, which is why a patch must be
 * shaped like the object's API input.
 */

export interface InlineMoney {
  amount: number
  currency: string
}

/** Every kind of value an inline editor can produce. `null` means cleared. */
export type InlineEditValue = string | number | boolean | InlineMoney | null

export type InlineEditType = 'text' | 'number' | 'money' | 'select' | 'bool' | 'date'

export interface InlineEditOption {
  value: string
  label: string
}

/** The payload that sets one field — shaped like the object's API input. */
export type InlineEditPatch = Record<string, unknown>

export interface InlineEditSpec<TData> {
  /**
   * Editor to open. A function picks per row, for fields that change shape
   * with the row — a line item objective is money on a budget and a plain
   * count on impressions.
   */
  type: InlineEditType | ((row: TData) => InlineEditType)
  /** The field's stored value, as the editor wants to receive it. */
  value: (row: TData) => InlineEditValue
  /**
   * The payload that sets the field to `value`. Build it from the row when the
   * API replaces a whole structure rather than merging into it, so the fields
   * that were not edited survive the save.
   */
  patch: (value: InlineEditValue, row: TData) => InlineEditPatch
  /** Choices for a `select` field. */
  options?: (row: TData) => InlineEditOption[]
  /** Currency a `money` field is denominated in. */
  currency?: (row: TData) => string
  /** Rows whose value cannot be changed — the cell stays read-only. */
  readOnly?: (row: TData) => boolean
  /** Returns a message to refuse the value with, or nothing to accept it. */
  validate?: (value: InlineEditValue, row: TData) => string | undefined
  /** Decimals shown for `money` and `number` values. */
  fractionDigits?: number
  min?: number
  max?: number
  step?: number | string
  maxLength?: number
  placeholder?: string
  /** Width of the open editor, as a Tailwind class. */
  editorClassName?: string
}

/** One staged cell: what it will become, and the payload that gets it there. */
export interface InlineCellEdit {
  value: InlineEditValue
  patch: InlineEditPatch
}

/** Every staged cell of one row, with the row they were staged against. */
export interface InlineRowEdit<TData> {
  row: TData
  cells: Record<string, InlineCellEdit>
}

/** Staged edits of a whole list, by row id. */
export type InlineEdits<TData> = Record<string, InlineRowEdit<TData>>

/** The editor a spec opens for a given row. */
export function inlineEditType<TData>(spec: InlineEditSpec<TData>, row: TData): InlineEditType {
  return typeof spec.type === 'function' ? spec.type(row) : spec.type
}

export function isInlineMoney(value: InlineEditValue): value is InlineMoney {
  return typeof value === 'object' && value !== null && 'amount' in value
}

/**
 * Whether an edit has brought a field back to what it already was. An empty
 * string, a null and an absent value all mean the same thing to the API, so
 * clearing a field that was never set is not a change.
 */
export function isSameInlineValue(a: InlineEditValue, b: InlineEditValue): boolean {
  const empty = (v: InlineEditValue) => v === null || v === undefined || v === ''
  if (empty(a) && empty(b)) return true
  if (isInlineMoney(a) && isInlineMoney(b)) return a.amount === b.amount && a.currency === b.currency
  return a === b
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Merges the patches of one row into a single payload. Nested objects are
 * merged branch by branch — a CPM edit and a CPC edit both live under
 * `bidSpecification` and have to arrive together — while arrays are replaced,
 * because the API reads a list it is sent as the whole list.
 */
export function mergePatches(patches: InlineEditPatch[]): InlineEditPatch {
  const merge = (base: Record<string, unknown>, patch: Record<string, unknown>) => {
    Object.entries(patch).forEach(([key, value]) => {
      const current = base[key]
      if (isPlainObject(current) && isPlainObject(value)) {
        merge(current, value)
      } else {
        base[key] = isPlainObject(value) ? { ...value } : value
      }
    })
    return base
  }
  return patches.reduce<InlineEditPatch>((acc, patch) => merge(acc, patch), {})
}

/** The rows a save has to send, each with its whole payload. */
export function pendingInlineRows<TData>(
  edits: InlineEdits<TData>,
): { id: string; row: TData; patch: InlineEditPatch }[] {
  return Object.entries(edits).map(([id, edit]) => ({
    id,
    row: edit.row,
    patch: mergePatches(Object.values(edit.cells).map((cell) => cell.patch)),
  }))
}

/** How many cells are staged, across every row. */
export function inlineEditCount<TData>(edits: InlineEdits<TData>): number {
  return Object.values(edits).reduce((total, edit) => total + Object.keys(edit.cells).length, 0)
}
